import Vue from 'vue'
import { levels } from '@/config/levels'
import { createBoard, cloneBoard, canPlaceBlock } from '@/utils/game'

const state = {
  currentLevel: 1,
  board: createBoard(),
  moveHistory: [],
  greyBlocksPlaced: 0,
  selectedBlock: null,
  gamePhase: 'grey', // 'grey' or 'blocks'
  blocks: {
    grey1: { size: [1, 1], color: 'grey', placed: false, position: null },
    grey2: { size: [1, 2], color: 'grey', placed: false, position: null },
    grey3: { size: [1, 3], color: 'grey', placed: false, position: null },
    blue1: { size: [1, 4], color: 'darkblue', placed: false, position: null },
    blue2: { size: [1, 5], color: 'darkblue', placed: false, position: null },
    green1: { size: [2, 2], color: 'green', placed: false, position: null },
    green2: { size: [3, 3], color: 'green', placed: false, position: null },
    red1: { size: [2, 3], color: 'red', placed: false, position: null },
    red2: { size: [2, 4], color: 'red', placed: false, position: null },
    yellow1: { size: [2, 5], color: 'yellow', placed: false, position: null },
    yellow2: { size: [4, 3], color: 'yellow', placed: false, position: null }
  }
}

const mutations = {
  SET_CURRENT_LEVEL(state, level) {
    state.currentLevel = level
  },
  SET_BOARD(state, board) {
    state.board = board
  },
  ADD_MOVE(state, move) {
    state.moveHistory.push(move)
  },
  REPLACE_MOVES(state, moves) {
    state.moveHistory = moves
  },
  REMOVE_LAST_MOVE(state) {
    state.moveHistory.pop()
  },
  INCREMENT_GREY_BLOCKS(state) {
    state.greyBlocksPlaced++
  },
  DECREMENT_GREY_BLOCKS(state) {
    state.greyBlocksPlaced--
  },
  SET_SELECTED_BLOCK(state, blockId) {
    state.selectedBlock = blockId
  },
  SET_GAME_PHASE(state, phase) {
    state.gamePhase = phase
  },
  UPDATE_BLOCK(state, { blockId, changes }) {
    state.blocks[blockId] = { ...state.blocks[blockId], ...changes }
  },
  RESET_BLOCKS(state) {
    Object.keys(state.blocks).forEach(blockId => {
      state.blocks[blockId].placed = false
      state.blocks[blockId].position = null
    })
    state.greyBlocksPlaced = 0
    state.gamePhase = 'grey'
    state.moveHistory = []
    state.selectedBlock = null
  },
  SET_BOARD_PREVIEW(state, { row, col, blockId }) {
    // 先清除所有预览
    for (let i = 0; i < 8; i++) {
      for (let j = 0; j < 8; j++) {
        if (state.board[i][j].preview) {
          Vue.set(state.board[i], j, { color: 'empty', blockId: null, preview: false })
        }
      }
    }

    const block = state.blocks[blockId]
    if (!block) return
    const [height, width] = block.size

    if (!canPlaceBlock(state.board, row, col, height, width)) return

    for (let i = 0; i < height; i++) {
      for (let j = 0; j < width; j++) {
        if (!state.board[row + i][col + j].blockId) {
          Vue.set(state.board[row + i], col + j, {
            color: block.color,
            blockId: null,
            preview: true
          })
        }
      }
    }
  },
  CLEAR_BOARD_PREVIEW(state) {
    for (let i = 0; i < 8; i++) {
      for (let j = 0; j < 8; j++) {
        if (state.board[i][j].preview) {
          Vue.set(state.board[i], j, { color: 'empty', blockId: null, preview: false })
        }
      }
    }
  }
}

const actions = {
  initLevel({ commit, state }, levelNumber) {
    const board = createBoard()
    commit('SET_BOARD', board)
    commit('RESET_BLOCKS')
    commit('SET_SELECTED_BLOCK', null)
    commit('SET_CURRENT_LEVEL', levelNumber)

    // 设置初始方块
    const level = levels[levelNumber]
    if (level && level.greyBlocks) {
      level.greyBlocks.forEach(block => {
        if (block.rotated) {
          const currentBlock = state.blocks[block.id]
          commit('UPDATE_BLOCK', {
            blockId: block.id,
            changes: { size: [currentBlock.size[1], currentBlock.size[0]] }
          })
        }
      })
    }
  },
  selectBlock({ commit }, blockId) {
    commit('SET_SELECTED_BLOCK', blockId)
  },
  placeBlock({ commit, state }, { row, col, blockId }) {
    const block = state.blocks[blockId]
    const [height, width] = block.size
    const board = cloneBoard(state.board)
    const wasPlaced = block.placed

    // 找到该方块在历史中的最后一条记录（即当前有效位置）
    const moveHistory = state.moveHistory
    let lastMoveIndex = -1
    for (let i = moveHistory.length - 1; i >= 0; i--) {
      if (moveHistory[i].blockId === blockId) {
        lastMoveIndex = i
        break
      }
    }

    const previousMove = lastMoveIndex >= 0 ? moveHistory[lastMoveIndex] : null
    if (previousMove) {
      // 用当时放置时记录的尺寸清理旧位置（size 可能因旋转改变）
      const prevSize = previousMove.size || [height, width]
      const [prevHeight, prevWidth] = prevSize
      for (let i = 0; i < prevHeight; i++) {
        for (let j = 0; j < prevWidth; j++) {
          board[previousMove.row + i][previousMove.col + j] = {
            color: 'empty',
            blockId: null,
            preview: false
          }
        }
      }
      // 移除该方块的所有历史记录
      const remainingMoves = moveHistory.filter(m => m.blockId !== blockId)
      commit('REPLACE_MOVES', remainingMoves)
    }

    // Place block on board
    for (let i = 0; i < height; i++) {
      for (let j = 0; j < width; j++) {
        board[row + i][col + j] = {
          color: block.color,
          blockId: blockId,
          preview: false
        }
      }
    }

    commit('SET_BOARD', board)
    commit('ADD_MOVE', { blockId, row, col, size: [height, width] })
    commit('UPDATE_BLOCK', {
      blockId,
      changes: { placed: true, position: { row, col } }
    })

    // 只有新放置（非移动）的灰色方块才计数
    if (blockId.startsWith('grey') && !wasPlaced) {
      commit('INCREMENT_GREY_BLOCKS')
      // INCREMENT_GREY_BLOCKS 已同步执行，greyBlocksPlaced 已 +1
      if (state.greyBlocksPlaced === 3) {
        commit('SET_GAME_PHASE', 'blocks')
      }
    }
  },
  undoLastMove({ commit, state }) {
    const lastMove = state.moveHistory[state.moveHistory.length - 1]
    if (!lastMove) return

    // Clear the block from the board
    const board = cloneBoard(state.board)
    const block = state.blocks[lastMove.blockId]
    const [height, width] = block.size
    
    for (let i = 0; i < height; i++) {
      for (let j = 0; j < width; j++) {
        board[lastMove.row + i][lastMove.col + j] = {
          color: 'empty',
          blockId: null,
          preview: false
        }
      }
    }

    commit('SET_BOARD', board)
    commit('UPDATE_BLOCK', {
      blockId: lastMove.blockId,
      changes: { placed: false, position: null }
    })

    if (lastMove.blockId.startsWith('grey')) {
      commit('DECREMENT_GREY_BLOCKS')
      if (state.gamePhase === 'blocks') {
        commit('SET_GAME_PHASE', 'grey')
      }
    }

    commit('REMOVE_LAST_MOVE')
  },
  rotateBlock({ commit, state }) {
    if (!state.selectedBlock) return
    
    const block = state.blocks[state.selectedBlock]
    if (block.size[0] === block.size[1]) return // Square blocks can't be rotated
    
    commit('UPDATE_BLOCK', {
      blockId: state.selectedBlock,
      changes: { size: [block.size[1], block.size[0]] }
    })
  }
}

const getters = {
  greyBlocks: state => {
    return Object.entries(state.blocks)
      .filter(([id]) => id.startsWith('grey'))
      .reduce((acc, [id, block]) => ({ ...acc, [id]: block }), {})
  },
  colorBlocks: state => {
    return Object.entries(state.blocks)
      .filter(([id]) => !id.startsWith('grey'))
      .reduce((acc, [id, block]) => ({ ...acc, [id]: block }), {})
  },
  isLevelComplete: state => {
    return state.board.every(row => 
      row.every(cell => cell.color !== 'empty')
    )
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
} 